import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  MenuItem, TextField, Checkbox, Alert, Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, regionsAPI, powerAddonsAPI, powerDefaultAddonsAPI, environmentsAPI } from '../../services/api';
import { Provider, Region, PowerAddon, PowerDefaultAddon, Environment } from '../../types';

const PowerDefaultAddons = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [powerAddons, setPowerAddons] = useState<PowerAddon[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<number | string>('');
  const [selectedRegion, setSelectedRegion] = useState<number | string>('');
  const [defaultAddons, setDefaultAddons] = useState<PowerDefaultAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<Record<string, boolean>>({});

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (selectedProvider) fetchRegions(); }, [selectedProvider]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedProvider && selectedRegion) fetchDefaultAddons(); }, [selectedProvider, selectedRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInitialData = async () => {
    try {
      const [providersRes, addonsRes, environmentsRes] = await Promise.all([providersAPI.getAll(), powerAddonsAPI.getAll(), environmentsAPI.getAll()]);
      const ibmProviders = providersRes.data.filter((p: Provider) => p.IsActive && p.ProviderName.toLowerCase().includes('ibm'));
      setProviders(ibmProviders);
      setPowerAddons(addonsRes.data.filter((a: PowerAddon) => a.IsActive));
      setEnvironments(environmentsRes.data);
      if (ibmProviders.length > 0) setSelectedProvider(ibmProviders[0].ProviderId);
    } catch { setError('Failed to load data'); } finally { setLoading(false); }
  };

  const fetchRegions = useCallback(async () => {
    try {
      const res = await regionsAPI.getAll(Number(selectedProvider));
      const active = res.data.filter((r: Region) => r.IsActive);
      setRegions(active);
      if (active.length > 0) setSelectedRegion(active[0].RegionId);
    } catch { setError('Failed to load regions'); }
  }, [selectedProvider]);

  const fetchDefaultAddons = useCallback(async () => {
    try {
      const res = await powerDefaultAddonsAPI.getAll(Number(selectedProvider), Number(selectedRegion));
      setDefaultAddons(res.data);
      setChanges({});
    } catch { setError('Failed to load power default addons'); }
  }, [selectedProvider, selectedRegion]);

  const getEnabled = (addonId: number, envId: number): boolean => {
    const key = `${addonId}-${envId}`;
    if (changes[key] !== undefined) return changes[key];
    const existing = defaultAddons.find(da => da.PowerAddonId === addonId && da.EnvironmentId === envId);
    return existing ? !!existing.IsEnabled : false;
  };

  const handleToggle = (addonId: number, envId: number) => {
    const key = `${addonId}-${envId}`;
    setChanges({ ...changes, [key]: !getEnabled(addonId, envId) });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([key, isEnabled]) => {
        const [powerAddonId, environmentId] = key.split('-').map(Number);
        return { powerAddonId, providerId: Number(selectedProvider), regionId: Number(selectedRegion), environmentId, isEnabled };
      });
      await powerDefaultAddonsAPI.batchSave(updates);
      setSuccess('Power default addons updated successfully');
      setChanges({});
      fetchDefaultAddons();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to update power default addons'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>Power Default Addons</Typography>
        <Typography variant="body2" color="text.secondary">Configure which Power addons are automatically applied to Power VMs per environment</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {providers.length === 0 ? (
        <Alert severity="info">No IBM providers configured. Power default addons are only available for IBM Cloud providers.</Alert>
      ) : (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select label="Provider" value={selectedProvider} onChange={(e) => setSelectedProvider(e.target.value)} sx={{ minWidth: 200 }}>
              {providers.map((p) => <MenuItem key={p.ProviderId} value={p.ProviderId}>{p.ProviderName}</MenuItem>)}
            </TextField>
            <TextField select label="Region" value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} disabled={!selectedProvider} sx={{ minWidth: 200 }}>
              {regions.map((r) => <MenuItem key={r.RegionId} value={r.RegionId}>{r.RegionName}</MenuItem>)}
            </TextField>
            <Button variant="contained" color="secondary" size="small" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={Object.keys(changes).length === 0} sx={{ ml: 'auto' }}>
              Save Changes ({Object.keys(changes).length})
            </Button>
          </Box>
          {selectedProvider && selectedRegion && environments.length > 0 && (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Power Addon</TableCell>
                    {environments.map((env) => (
                      <TableCell key={env.EnvironmentId} align="center" sx={{ fontWeight: 'bold', minWidth: 90 }}>{env.EnvironmentCode.toUpperCase()}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {powerAddons.map((addon) => (
                    <TableRow key={addon.PowerAddonId} hover>
                      <TableCell>
                        {addon.PowerAddonName}
                        {addon.Description && <Typography variant="caption" display="block" color="text.secondary">{addon.Description}</Typography>}
                      </TableCell>
                      {environments.map((env) => (
                        <TableCell key={`${addon.PowerAddonId}-${env.EnvironmentId}`} align="center">
                          <Checkbox size="small" checked={getEnabled(addon.PowerAddonId, env.EnvironmentId)} onChange={() => handleToggle(addon.PowerAddonId, env.EnvironmentId)} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default PowerDefaultAddons;
